import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  Eye,
  Zap,
  ArrowLeft,
  Download,
  AlertTriangle,
  Loader2,
  Lock,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const REQUIRED_COLUMNS = [
  "tenant_full_name",
  "tenant_phone",
  "tenant_email",
  "tenant_unit",
  "tenant_building",
  "year",
  "make",
  "model",
  "color",
  "license_plate",
  "state",
  "sticker_color",
  "permit_type",
];

const DUPLICATE_LABEL = {
  plate_in_property: "Plate already exists in this property",
  plate_in_corporation: "Plate already exists in another property of the same corporation",
  duplicate_in_upload: "Plate appears more than once in this upload",
  tenant_plate_duplicate_in_upload: "Same tenant + plate appears more than once in this upload",
};

const SAMPLE_CSV = `tenant_full_name,tenant_phone,tenant_email,tenant_unit,tenant_building,year,make,model,color,license_plate,state,sticker_color,permit_type
Alicia Chen,+1-415-555-0101,alicia@example.com,101,A,2022,Tesla,Model 3,White,8SCM221,CA,#0F8F4F,Resident
Alicia Chen,+1-415-555-0101,alicia@example.com,101,A,2019,Honda,Civic,Blue,7XYZ102,CA,#0F8F4F,Resident
Marcus Johnson,+1-415-555-0102,marcus@example.com,202,A,2021,Toyota,Corolla,Silver,8ABC331,CA,#0F8F4F,Resident
`;

export default function BulkImport() {
  const { activeProperty } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  // index -> "skip" | "import" (force import, override duplicates)
  const [rowDecision, setRowDecision] = useState({});

  const onFile = (file) => {
    setSummary(null);
    setRows([]);
    setParseErrors([]);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (res) => {
        const missing = REQUIRED_COLUMNS.filter(
          (c) => !res.meta.fields?.includes(c)
        ).filter((c) =>
          // tenant_phone, tenant_email, tenant_building, sticker_color, permit_type are optional
          ![
            "tenant_phone",
            "tenant_email",
            "tenant_building",
            "sticker_color",
            "permit_type",
          ].includes(c)
        );
        if (missing.length) {
          toast.error(`Missing columns: ${missing.join(", ")}`);
          return;
        }
        const cleaned = res.data
          .map((r) => Object.fromEntries(
            Object.entries(r).map(([k, v]) => [k, (v || "").toString().trim()])
          ))
          .filter((r) => r.tenant_full_name && r.license_plate);
        setRows(cleaned);
        setParseErrors(res.errors || []);
        toast.success(`Parsed ${cleaned.length} rows`);
      },
      error: (e) => toast.error(`Parse error: ${e.message}`),
    });
  };

  const validate = async () => {
    if (!rows.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/bulk-import", {
        property_id: activeProperty.id,
        rows,
        activate: true,
        dry_run: true,
      });
      setSummary(data.summary);
      // Default: skip rows that have any duplicate flag
      const dec = {};
      (data.summary.duplicates || []).forEach((d) => {
        const idx = d.row - 1;
        if (!(idx in dec)) dec[idx] = "skip";
      });
      setRowDecision(dec);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const filterRowsForCommit = () =>
    rows.filter((_, i) => rowDecision[i] !== "skip");

  const commit = async (activate) => {
    const filtered = filterRowsForCommit();
    if (!filtered.length) {
      toast.error("No rows selected to import");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/bulk-import", {
        property_id: activeProperty.id,
        rows: filtered,
        activate,
        dry_run: false,
      });
      toast.success(
        `${data.summary.vehicles_to_create} vehicles created${
          activate ? " & activated" : " (saved as Inactive)"
        }`
      );
      setSummary(null);
      setRows([]);
      setRowDecision({});
      nav("/dashboard/vehicles");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Subscription required — redirecting to Billing");
        nav("/dashboard/billing");
        return;
      }
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "scanmint-bulk-template.csv";
    a.click();
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => nav(-1)} className="mb-2" data-testid="bulk-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>
      <div>
        <div className="mono-tiny text-[#71717A]">Bulk import</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
          Upload a CSV of tenants &amp; vehicles
        </h1>
        <p className="text-[#71717A] text-sm mt-1">
          Tenants are matched by full name + unit. New tenants are created. One row = one vehicle.
        </p>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
            <div>
              <div className="mono-tiny text-[#71717A]">CSV file</div>
              <div className="font-display font-bold">Upload &amp; preview</div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={downloadSample}
            className="h-10"
            data-testid="bulk-download-template"
          >
            <Download className="w-4 h-4 mr-2" /> Download template
          </Button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
        />
        <button
          onClick={() => fileRef.current?.click()}
          data-testid="bulk-pick-file"
          className="w-full border-2 border-dashed border-[#E4E4E7] rounded-md p-10 flex flex-col items-center justify-center gap-2 hover:bg-[#F4F4F5] transition-colors"
        >
          <Upload className="w-6 h-6 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="font-medium">Drop a CSV here or click to choose</div>
          <div className="mono-tiny text-[#71717A]">UTF-8 · max 1,000 rows recommended</div>
        </button>

        <div className="mono-tiny text-[#71717A]">Required columns</div>
        <div className="flex flex-wrap gap-1.5 -mt-3">
          {REQUIRED_COLUMNS.map((c) => (
            <span
              key={c}
              className="text-[11px] font-mono bg-[#F4F4F5] border border-[#E4E4E7] rounded px-2 py-0.5"
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Preview */}
      {rows.length > 0 && (
        <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden" data-testid="bulk-preview">
          <div className="p-5 border-b border-[#E4E4E7] flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="mono-tiny text-[#71717A]">Preview</div>
              <div className="font-display font-bold">{rows.length} rows ready</div>
            </div>
            <Button
              onClick={validate}
              disabled={busy}
              className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-11"
              data-testid="bulk-validate"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              Validate &amp; estimate
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
                <tr>
                  {["Tenant", "Unit", "Vehicle", "Plate", "State", "Permit"].map((h) => (
                    <th key={h} className="text-left mono-tiny text-[#71717A] px-4 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4E4E7]">
                {rows.slice(0, 30).map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2.5">{r.tenant_full_name}</td>
                    <td className="px-4 py-2.5 text-[#71717A]">{r.tenant_unit}</td>
                    <td className="px-4 py-2.5">{r.year} {r.make} {r.model}</td>
                    <td className="px-4 py-2.5 font-mono">{r.license_plate}</td>
                    <td className="px-4 py-2.5">{r.state}</td>
                    <td className="px-4 py-2.5 text-[#71717A]">{r.permit_type || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 30 && (
              <div className="p-3 text-center mono-tiny text-[#71717A] border-t border-[#E4E4E7]">
                + {rows.length - 30} more rows
              </div>
            )}
          </div>
        </div>
      )}

      {parseErrors.length > 0 && (
        <div className="bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-md p-4 flex gap-3">
          <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5" strokeWidth={1.5} />
          <div className="text-sm text-[#B91C1C]">
            <div className="font-medium">{parseErrors.length} parse warning(s)</div>
            {parseErrors.slice(0, 3).map((e, i) => (
              <div key={i} className="mono-tiny">Row {e.row}: {e.message}</div>
            ))}
          </div>
        </div>
      )}

      {/* Summary modal-style block */}
      {summary && (
        <div
          className="bg-white border-2 border-[#0F8F4F] rounded-xl p-6 md:p-8"
          data-testid="bulk-summary"
        >
          <div className="flex items-center gap-3 mb-5">
            <CheckCircle2 className="w-5 h-5 text-[#00C16E]" strokeWidth={1.5} />
            <div>
              <div className="mono-tiny text-[#71717A]">Validation</div>
              <div className="font-display text-2xl font-bold">
                You are about to activate {summary.vehicles_to_create} vehicles.
              </div>
              <div className="text-sm text-[#71717A] mt-1">
                Estimated monthly charge:{" "}
                <span className="font-display font-bold text-[#09090B]">
                  ${summary.estimated_monthly_delta.toFixed(2)}
                </span>
                {" "}— {summary.billable_new_vehicles} billable / {summary.free_new_vehicles} free
                (2nd per tenant)
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryStat label="New tenants" value={summary.tenants_to_create} />
            <SummaryStat label="Matched tenants" value={summary.tenants_matched} />
            <SummaryStat label="Vehicles" value={summary.vehicles_to_create} />
            <SummaryStat
              label="Plan"
              value={summary.plan?.toUpperCase()}
            />
          </div>

          {summary.errors?.length > 0 && (
            <div className="mt-5 bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-md p-4 text-sm text-[#B91C1C]" data-testid="bulk-errors">
              <div className="font-medium mb-1">{summary.errors.length} row(s) skipped:</div>
              {summary.errors.slice(0, 5).map((e, i) => (
                <div key={i} className="mono-tiny">Row {e.row}: {e.error}</div>
              ))}
            </div>
          )}

          {/* Duplicates — let user pick Skip / Import per affected row */}
          {summary.duplicates?.length > 0 && (
            <div
              className="mt-5 bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-md p-4"
              data-testid="bulk-duplicates"
            >
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#92400E]" strokeWidth={1.5} />
                <div className="font-medium text-[#92400E]">
                  {summary.duplicates.length} potential duplicate(s) detected
                </div>
              </div>
              <div className="text-xs text-[#92400E] mb-3">
                Default: <b>Skip</b>. Toggle to <b>Import</b> to override and create the row anyway.
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(
                  summary.duplicates.reduce((acc, d) => {
                    (acc[d.row] = acc[d.row] || []).push(d);
                    return acc;
                  }, {})
                ).map(([rowNum, flags]) => {
                  const idx = parseInt(rowNum, 10) - 1;
                  const row = rows[idx];
                  const decision = rowDecision[idx] || "skip";
                  return (
                    <div
                      key={rowNum}
                      className="bg-white border border-[#E4E4E7] rounded-md p-3 flex items-center justify-between gap-3"
                      data-testid={`bulk-dup-row-${rowNum}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium">
                          Row {rowNum} · {row?.tenant_full_name} · plate{" "}
                          <span className="font-mono">{row?.license_plate}</span>
                        </div>
                        <div className="mono-tiny text-[#71717A]">
                          {flags.map((f) => DUPLICATE_LABEL[f.kind] || f.kind).join(" · ")}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => setRowDecision((d) => ({ ...d, [idx]: "skip" }))}
                          data-testid={`bulk-dup-skip-${rowNum}`}
                          className={`px-3 py-1.5 rounded text-xs font-semibold ${
                            decision === "skip"
                              ? "bg-[#09090B] text-white"
                              : "bg-[#F4F4F5] text-[#71717A]"
                          }`}
                        >
                          Skip
                        </button>
                        <button
                          onClick={() => setRowDecision((d) => ({ ...d, [idx]: "import" }))}
                          data-testid={`bulk-dup-import-${rowNum}`}
                          className={`px-3 py-1.5 rounded text-xs font-semibold ${
                            decision === "import"
                              ? "bg-[#0F8F4F] text-white"
                              : "bg-[#F4F4F5] text-[#71717A]"
                          }`}
                        >
                          Import
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mono-tiny text-[#71717A] mt-3">
                {Object.values(rowDecision).filter((v) => v === "skip").length} skipped ·{" "}
                {Object.values(rowDecision).filter((v) => v === "import").length} forced import ·{" "}
                {filterRowsForCommit().length} rows ready to commit
              </div>
            </div>
          )}

          {/* Subscription gate hint */}
          {!summary.can_activate && (
            <div
              className="mt-5 bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-md p-4 flex items-start gap-3"
              data-testid="bulk-subscription-required"
            >
              <Lock className="w-4 h-4 text-[#92400E] mt-0.5" strokeWidth={1.5} />
              <div className="text-sm text-[#92400E]">
                <b>Subscription required to Activate All.</b> You can still <b>Review First</b> to
                save the rows as Inactive — then subscribe and activate from the Vehicles list.
              </div>
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-2">
            <Button
              onClick={() => commit(true)}
              disabled={busy || !summary.vehicles_to_create || !summary.can_activate}
              className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              data-testid="bulk-activate-all"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Activate All
            </Button>
            <Button
              onClick={() => commit(false)}
              disabled={busy || !summary.vehicles_to_create}
              variant="outline"
              className="h-12"
              data-testid="bulk-review-first"
            >
              <Eye className="w-4 h-4 mr-2" /> Review First (save as Inactive)
            </Button>
            <Button
              onClick={() => setSummary(null)}
              variant="ghost"
              className="h-12"
              data-testid="bulk-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="border border-[#E4E4E7] rounded-md p-4">
      <div className="mono-tiny text-[#71717A]">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
